import {Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges} from '@angular/core';
import {ReportUserGenerated} from "../../../../core/model-store/models/report-user-generated";
import {MediaHelperService} from "../../../../core/media-helper.service";

/** The backend maps 'medium' to 600x600 px, enough for a printed grid cell. */
const GRID_REPORT_IMAGE_SIZE = 'medium';

@Component({
    selector: 'grid-report',
    templateUrl: './grid-report.component.html',
    styleUrls: ['./grid-report.component.scss'],
    standalone: false
})
export class GridReportComponent implements OnChanges {
  private readonly mediaHelper: MediaHelperService = inject(MediaHelperService);

  @Input() reportTemplate: ReportUserGenerated;
  @Input() extractedArtifacts: any;
  @Input() viewOptionsCheckboxValues: any;
  @Input() preview: boolean = false;
  @Input() numColumnsInReport = 2;

  /** Emits when every image has loaded or failed, so the PDF can be printed. */
  @Output() imagesReady = new EventEmitter<void>();

  imageUrls = signal(new Map<string, string>());

  private pendingImages = 0;
  private latestUrlRequest = 0;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.extractedArtifacts) {
      this.loadImageUrls().then();
    }
  }

  isPromptSelected() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'vis_ledetekster')?.value;
  }

  isOnlyPhotosSelected() {
    return this.viewOptionsCheckboxValues.find((option) => option.key === 'kun_bilder')? this.viewOptionsCheckboxValues.find((option) => option.key === 'kun_bilder').value : false;
  }

  imageUrlFor(artifact: any): string | null {
    return this.imageUrls().get(artifact.thumbnailImage.image_id) ?? null;
  }

  onImageSettled() {
    this.pendingImages--;
    this.emitIfAllImagesSettled();
  }

  private async loadImageUrls() {
    const request = ++this.latestUrlRequest;
    const artifacts: any[] = this.extractedArtifacts ?? [];
    const urls = await this.fetchImageUrls(this.uniqueImageIds(artifacts));
    if (request !== this.latestUrlRequest) {
      return;
    }
    this.imageUrls.set(urls);
    this.pendingImages = artifacts
      .filter(artifact => urls.has(artifact.thumbnailImage?.image_id))
      .length;
    this.emitIfAllImagesSettled();
  }

  private uniqueImageIds(artifacts: any[]): string[] {
    const imageIds = artifacts
      .map(artifact => artifact.thumbnailImage?.image_id)
      .filter(imageId => !!imageId);
    return [...new Set<string>(imageIds)];
  }

  private async fetchImageUrls(imageIds: string[]) {
    const urls = new Map<string, string>();
    if (imageIds.length === 0) {
      return urls;
    }
    const res = await this.mediaHelper.getImageUrlsFromImageIds(
      imageIds, [GRID_REPORT_IMAGE_SIZE]);
    imageIds.forEach(imageId => {
      const url = res?.[imageId]?.[GRID_REPORT_IMAGE_SIZE];
      if (url) {
        urls.set(imageId, url);
      }
    });
    return urls;
  }

  private emitIfAllImagesSettled() {
    if (this.pendingImages === 0) {
      this.imagesReady.emit();
    }
  }

}
